import { isAxiosError } from 'axios';
import { AppError } from '@/utils/api';
import { AuthenticatedHttp } from '../../http/axios';

export const getPlatforms = async (p) =>
  AuthenticatedHttp.get(`/app/platforms?limit=20`).then((response) => response.data);

export const createPlatformConfig = async ({ platformId, name, url, products, events, fields }) =>
  AuthenticatedHttp.post(`/app/platforms/${platformId}/app/`, {
    name,
    url,
    products,
    events,
    fields,
  })
    .then((response) => response.data)
    .catch((error) => {
      throw new AppError(error);
    });

export const updatePlatformConfig = async ({ appId, ...data }) =>
  AuthenticatedHttp.patch(`/app/${appId}/`, data)
    .then((response) => response.data)
    .catch((error) => {
      throw new AppError(error);
    });

export const deletePlatformConfig = async ({ appId }) =>
  AuthenticatedHttp.delete(`/app/${appId}/`)
    .then((response) => response.data)
    .catch((error) => {
      throw new AppError(error);
    });

export const getPlatformConfigs = async ({
  platformId,
  page = 1,
  limit = 10,
  search = '',
  filters = {},
}) =>
  AuthenticatedHttp.get(`/app/platforms/${platformId}/app/`, {
    params: {
      page,
      limit,
      search,
      ...(filters?.products && { products: filters.products }),
    },
  })
    .then((response) => response.data)
    .catch(() => []);

export const getActiveCampaignData = async ({ url, token }) => {
  const promises = [
    AuthenticatedHttp.get(
      `/app/active_campaign/redirect/?endpoint=lists&token=${token}&url=${url}`
    ).then(({ data }) => data.lists),
    AuthenticatedHttp.get(
      `/app/active_campaign/redirect/?endpoint=tags&token=${token}&url=${url}`
    ).then(({ data }) => data.tags),
  ];

  const [lists, tags] = await Promise.all(promises);

  return {
    lists,
    tags,
  };
};

export const getMemberkitData = async (key) => {
  const promises = [
    AuthenticatedHttp.get(`/app/memberkit/redirect/?endpoint=classrooms&api_key=${key}`).then(
      ({ data }) => data
    ),
    AuthenticatedHttp.get(
      `/app/memberkit/redirect/?endpoint=membership_levels&api_key=${key}`
    ).then(({ data }) => data),
  ];

  const [courses, subscriptions] = await Promise.all(promises);

  return {
    courses,
    subscriptions,
  };
};

export const sendWebhookTest = async ({ appId, events }) => {
  const promises = events.map(async (eventId) =>
    AuthenticatedHttp.post(`/app/events/${appId}/test/?event=${eventId}`)
  );

  await Promise.all(promises);
};

export const TEST_EVENT_OUTCOME = Object.freeze({
  SENT: 'sent',
  FAILED: 'failed',
});

const toTestEventResult = (eventId, settled) => {
  if (settled.status === 'fulfilled') {
    return { eventId, outcome: TEST_EVENT_OUTCOME.SENT, httpStatus: settled.value?.status ?? null };
  }

  const { reason } = settled;

  // Só erro do axios é resultado do envio. Qualquer outro é bug do nosso código, e tratá-lo como
  // falha de rede o esconderia atrás de um resultado plausível.
  if (!isAxiosError(reason)) {
    throw reason;
  }

  const { status = null, data } = reason.response || {};
  const body = data && typeof data === 'object' ? data : {};

  return {
    eventId,
    outcome: TEST_EVENT_OUTCOME.FAILED,
    httpStatus: status,
    statusCode: Number.isInteger(body.status_code) ? body.status_code : null,
    response: body.response,
    detail: typeof body.detail === 'string' ? body.detail : null,
  };
};

// `allSettled`, e não `all`: com `all`, a primeira falha rejeita o lote e o resultado dos outros
// eventos se perde. O `sendWebhookTest` continua rejeitando porque o `testWebhook` do
// `useWebhooks` depende disso.
export const sendAppTestEvents = async ({ appId, events = [] }) => {
  const settled = await Promise.allSettled(
    events.map((eventId) => AuthenticatedHttp.post(`/app/events/${appId}/test/?event=${eventId}`))
  );

  return settled.map((result, index) => toTestEventResult(events[index], result));
};

export const fetchAppEventHistory = async ({ appId, page = 1, limit = 100 }) => {
  const response = await AuthenticatedHttp.get(`/app/events/${appId}/history/`, {
    params: {
      page,
      ...(limit && { limit }),
    },
  });
  return response.data;
};

export const resendEventHistory = async ({ eventHistoryId }) => {
  const response = await AuthenticatedHttp.post(`/app/events/resend/${eventHistoryId}/`);
  return response.data;
};
